import type {
  PortalDelivery,
  PortalItem,
  PortalOrder,
  PortalProject,
  PortalStage,
  PortalWarrantyClaimStatus,
} from '@/api/client'

/** The four stops every item moves through. Cancelled and returned sit outside the rail. */
export const PROGRESS: PortalStage[] = ['ordered', 'processing', 'on_the_way', 'delivered']

export const STAGE_LABEL: Record<PortalStage, string> = {
  ordered: 'Order received',
  processing: 'Processing',
  on_the_way: 'On the way',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
  returned: 'Return in progress',
}

/** Backend `date` fields are calendar days; parse them without a timezone shift. */
export function parseDay(day: string): Date {
  const [y = 0, m = 1, d = 1] = day.split('-').map(Number)
  return new Date(y, m - 1, d)
}

const shortDay = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' })
const longDay = new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
const fullDay = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' })

export const formatDay = (day: string) => shortDay.format(parseDay(day))
export const formatWeekday = (day: string) => longDay.format(parseDay(day))
export const formatFullDay = (day: string) => fullDay.format(parseDay(day))

const longWeekday = new Intl.DateTimeFormat('en-US', { weekday: 'long' })
const longMonth = new Intl.DateTimeFormat('en-US', { month: 'long' })
const ORDINAL: Record<number, string> = { 1: 'st', 2: 'nd', 3: 'rd' }

/** "Monday, October 26th": the arrival headline, written out in full. */
export function formatSpelledDay(day: string): string {
  const date = parseDay(day)
  const n = date.getDate()
  const suffix = n >= 11 && n <= 13 ? 'th' : (ORDINAL[n % 10] ?? 'th')
  return `${longWeekday.format(date)}, ${longMonth.format(date)} ${n}${suffix}`
}

const COMING: PortalStage[] = ['ordered', 'processing', 'on_the_way']

/** Whether these deliveries land on different days, so no single date speaks for the order (USA-1398). */
function differentDays(deliveries: PortalDelivery[]): boolean {
  return new Set(deliveries.map((d) => d.delivery_on ?? null)).size > 1
}

/** The deliveries still coming, when they arrive on different days. */
function separateArrivals(order: PortalOrder): PortalDelivery[] {
  const coming = order.deliveries.filter((d) => COMING.includes(d.stage))
  return differentDays(coming) ? coming : []
}

/** The order's headline: when everything arrives, or where it stands when no one date fits. */
export function orderHeadline(order: PortalOrder): string {
  if (order.stage === 'delivered') {
    const delivered = order.deliveries.filter((d) => d.stage === 'delivered')
    return order.delivery_on && !differentDays(delivered)
      ? `Delivered ${formatDay(order.delivery_on)}`
      : 'Delivered'
  }
  if (!PROGRESS.includes(order.stage)) return STAGE_LABEL[order.stage]
  const separate = separateArrivals(order)
  if (separate.length > 0) return `Arriving in ${separate.length} deliveries`
  return order.delivery_on ? `Arriving ${formatSpelledDay(order.delivery_on)}` : STAGE_LABEL[order.stage]
}

/** Shown under an arrival headline whose dates are still estimates. */
export function orderHeadlineNote(order: PortalOrder): string | null {
  const arriving = order.stage !== 'delivered' && PROGRESS.includes(order.stage)
  const estimated = order.delivery_on ? order.delivery_estimated : separateArrivals(order).length > 0
  return arriving && estimated
    ? 'Processing time and delivery dates are estimates and can’t be guaranteed.'
    : null
}

export const SITE_NAME = 'USA Restaurant Suppliers'

/** The browser tab title: the order number, or the tracker's name when there are several orders. */
export function trackerTitle(project: PortalProject): string {
  const [only, ...rest] = project.orders
  const name = only?.order_number && rest.length === 0 ? `Order #${only.order_number}` : 'Your orders'
  return `${name} | ${SITE_NAME}`
}

/** The order's details under its headline: number, placed date, and the date the customer needs it by. */
export function orderMeta(order: PortalOrder): string[] {
  return [
    order.order_number ? `Order #${order.order_number}` : null,
    `Placed ${formatFullDay(order.placed_on)}`,
    order.deliver_by && order.stage !== 'delivered' ? `Needed by ${formatWeekday(order.deliver_by)}` : null,
  ].filter((part): part is string => part !== null)
}

export type DeliveryDate = { label: string; day: string; estimated: boolean }

/** The two dates a delivery moves through: leaving the manufacturer, and reaching the customer. */
export function deliveryDates(delivery: PortalDelivery): DeliveryDate[] {
  const dates: DeliveryDate[] = []
  if (delivery.ship_on) {
    dates.push({
      label: delivery.ship_estimated ? (delivery.pickup ? 'Pickup' : 'Ships') : 'Shipped',
      day: delivery.ship_on,
      estimated: delivery.ship_estimated,
    })
  }
  if (delivery.delivery_on) {
    dates.push({
      label: delivery.stage === 'delivered' ? 'Delivered' : 'Arrives',
      day: delivery.delivery_on,
      estimated: delivery.delivery_estimated,
    })
  }
  return dates
}

/** How many items sit at each stop, counted only when the order is split across stops. */
export function stageCounts(order: PortalOrder): Partial<Record<PortalStage, number>> {
  const counts: Partial<Record<PortalStage, number>> = {}
  for (const delivery of order.deliveries) {
    counts[delivery.stage] = (counts[delivery.stage] ?? 0) + delivery.items.length
  }
  return Object.keys(counts).length > 1 ? counts : {}
}

/** Most useful first: what is moving, then what is coming, then what is done. */
const GROUP_ORDER: PortalStage[] = [
  'on_the_way',
  'processing',
  'ordered',
  'delivered',
  'returned',
  'cancelled',
]

export function sortedDeliveries(order: PortalOrder): PortalDelivery[] {
  return [...order.deliveries].sort(
    (a, b) =>
      GROUP_ORDER.indexOf(a.stage) - GROUP_ORDER.indexOf(b.stage) ||
      (a.delivery_on ?? '9999').localeCompare(b.delivery_on ?? '9999'),
  )
}

/** `tel:` link for a US phone number as staff type it. */
export function phoneHref(phone: string): string {
  const digits = phone.replace(/\D/g, '')
  return `tel:+${digits.length === 10 ? `1${digits}` : digits}`
}

const OPEN_CLAIM: PortalWarrantyClaimStatus[] = ['submitted', 'with_manufacturer', 'approved']

/** Delivered items a claim can be filed for: each order line once, minus those with an open claim. */
export function claimableItems(order: PortalOrder): PortalItem[] {
  const open = new Set(
    order.warranty_claims.filter((claim) => OPEN_CLAIM.includes(claim.status)).map((claim) => claim.line_id),
  )
  const seen = new Set<number>()
  const items: PortalItem[] = []
  for (const delivery of order.deliveries) {
    for (const item of delivery.items) {
      if (item.line_id == null || seen.has(item.line_id) || open.has(item.line_id)) continue
      seen.add(item.line_id)
      items.push(item)
    }
  }
  return items
}
